import { useCallback, useEffect, useRef, useState } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { downloadBlob } from "@/shared/utils/downloadBlob";
import { canShareFiles, shareFile } from "@/shared/utils/shareImage";
import { colocaToast } from "@/shared/utils/toast";

import { WalletTopUpsService } from "../services/walletTopUps.service";
import type { WalletTopUp, WalletTopUpReceiptFile } from "../types/walletTopUps.types";

const PDF = "application/pdf";

export interface UseWalletTopUpReceiptResult {
  canShare: boolean;
  busy: "download" | "share" | null;
  download: () => Promise<void>;
  share: () => Promise<void>;
}

/**
 * Download or share the PDF receipt. Where the platform can share files the PDF is fetched on mount: Safari
 * only opens the share sheet inside the click's own gesture, which an `await` on the network would end.
 */
export function useWalletTopUpReceipt(
  topUp: Pick<WalletTopUp, "id" | "reference">,
  { prefetch = true }: { prefetch?: boolean } = {},
): UseWalletTopUpReceiptResult {
  const { t } = useTranslations("walletTopUps");
  const { id, reference } = topUp;
  const [canShare] = useState(() => canShareFiles(PDF));
  const [busy, setBusy] = useState<"download" | "share" | null>(null);
  const fileRef = useRef<Promise<WalletTopUpReceiptFile> | null>(null);

  const load = useCallback(() => {
    fileRef.current ??= WalletTopUpsService.receipt({ id, reference }).catch((error: unknown) => {
      fileRef.current = null;
      throw error;
    });
    return fileRef.current;
  }, [id, reference]);

  useEffect(() => {
    fileRef.current = null;
    if (canShare && prefetch) void load().catch(() => undefined);
  }, [canShare, load, prefetch]);

  const download = useCallback(async () => {
    setBusy("download");
    try {
      const file = await load();
      downloadBlob(file.blob, file.filename);
    } catch (error) {
      colocaToast.error(t("receipt_error", { message: HttpApiService.getErrorMessage(error) }));
    } finally {
      setBusy(null);
    }
  }, [load, t]);

  const share = useCallback(async () => {
    setBusy("share");
    try {
      const file = await load();
      await shareFile(file.blob, {
        fileName: file.filename,
        title: t("receipt_share_title", { reference }),
        type: PDF,
      });
    } catch (error) {
      colocaToast.error(t("receipt_error", { message: HttpApiService.getErrorMessage(error) }));
    } finally {
      setBusy(null);
    }
  }, [load, reference, t]);

  return { canShare, busy, download, share };
}
